import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { notification, Spin } from "antd"
import React, { ReactNode, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Modal } from "../Modal/Modal"

export enum NotificationType {
    Success = "success",
    Error = "error",
}

export const openNotification = (type: NotificationType, title: string, message?: Maybe<string>, duration?: number) => {
    notification[type]({
        message: title,
        description: message,
        placement: "bottomRight",
        duration: duration ?? 3,
    })
}

type DetailModalState = {
    title: string
    message: ReactNode
} | null

let setDetailModalState: ((state: DetailModalState) => void) | null = null

const openDetailModal = (title: string, message: ReactNode) => {
    setDetailModalState?.({ title: title, message: message })
}

export const NotificationDetailModalPortal = () => {
    const [state, setState] = useState<DetailModalState>(null)

    useEffect(() => {
        setDetailModalState = setState
        return () => {
            setDetailModalState = null
        }
    }, [])

    if (!state) {
        return null
    }

    return createPortal(
        <Modal
            isVisible={true}
            onClose={() => setState(null)}
            allowBackdropClose={true}
            header={{ title: state.title }}
            size="default">
            <div className="max-h-[60vh] overflow-y-auto whitespace-pre-wrap">{state.message}</div>
        </Modal>,
        document.body
    )
}

type NotificationDescriptionProps = {
    message: string
    onOpenDetail?: () => void
}

const NotificationDescriptionWithDetail = ({ message, onOpenDetail }: NotificationDescriptionProps) => {
    const [isOverflowing, setIsOverflowing] = useState(false)
    const textRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (textRef.current) {
            setIsOverflowing(textRef.current.scrollHeight > textRef.current.clientHeight)
        }
    }, [message])

    return (
        <>
            <div ref={textRef} className="line-clamp-3">
                {message}
            </div>
            {isOverflowing && (
                <a className="mt-1 block cursor-pointer text-blue-600 underline" onClick={onOpenDetail}>
                    Show more
                </a>
            )}
        </>
    )
}

export const openNotificationWithModalDetail = (
    type: NotificationType,
    title: string,
    message?: Maybe<string>,
    modalContent?: ReactNode | null,
    duration?: number
) => {
    notification[type]({
        message: title,
        description: message ? (
            <NotificationDescriptionWithDetail
                message={message}
                onOpenDetail={() => openDetailModal(title, modalContent ?? message)}
            />
        ) : undefined,
        placement: "bottomRight",
        duration: duration ?? 3,
    })
}

const LOADING_NOTIFICATION_KEY = "LOADING_NOTIFICATION_KEY"
const LOADING_NOTIFICATION_CLASS = "loading-notification-no-close"

export const openLoadingNotification = (title?: Maybe<string>, message?: Maybe<string>, duration?: number) => {
    notification.open({
        message: title ?? "Saving data...",
        description: message,
        key: LOADING_NOTIFICATION_KEY,
        className: LOADING_NOTIFICATION_CLASS,
        placement: "bottomRight",
        icon: <Spin size="small" />,
        duration: duration ?? 10, // Although loading should be closed manually with 'closeLoadingNotification', we should keep some timeout
    })
}

export const closeLoadingNotification = () => {
    notification.close(LOADING_NOTIFICATION_KEY)
}
